'use client'

import { useState, useEffect } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import type { ToolState } from '@/types/radar'
import { GRADE_COLORS, elapsed } from '@/lib/radar/scoring'

export function ToolResultCard({
  name,
  icon,
  color,
  state,
  isExpanded,
  onClick,
  onRetry,
  estSeconds,
  animIndex = 0,
}: {
  toolKey: string
  name: string
  icon: React.ReactNode
  color: string
  state: ToolState
  isExpanded: boolean
  onClick: () => void
  onRetry?: () => void
  estSeconds: number
  animIndex?: number
}) {
  const done = state.status === 'done'
  const err = state.status === 'error'
  const running = state.status === 'running'

  const [runTime, setRunTime] = useState(0)
  useEffect(() => {
    if (!running || !state.startedAt) {
      setRunTime(0)
      return
    }
    const interval = setInterval(
      () => setRunTime(Math.floor((Date.now() - state.startedAt!) / 1000)),
      500
    )
    return () => clearInterval(interval)
  }, [running, state.startedAt])

  return (
    <div
      className={`rounded-xl border p-4 text-left transition-all ${done ? 'animate-[fadeSlideIn_0.3s_ease-out_forwards]' : ''} ${isExpanded ? 'border-border' : 'border-border'} ${done ? 'bg-card' : 'bg-background'} ${state.status === 'skipped' ? 'opacity-30' : ''}`}
      style={{
        borderColor: isExpanded ? `${color}50` : undefined,
        cursor: done ? 'pointer' : 'default',
        animationDelay: done ? `${animIndex * 80}ms` : undefined,
      }}
      onClick={done ? onClick : undefined}
      role={done ? 'button' : undefined}
      tabIndex={done ? 0 : undefined}
    >
      <div className='flex items-baseline justify-between mb-2'>
        <div className='flex items-baseline gap-2'>
          {running ? (
            <Loader2 size={14} className='animate-spin' style={{ color }} />
          ) : (
            icon
          )}
          <span className='text-xs font-semibold text-muted-foreground'>
            {name}
          </span>
        </div>
        {state.startedAt && done && (
          <span className='text-[11px] font-mono tabular-nums text-muted-foreground'>
            {elapsed(state.startedAt, state.finishedAt)}
          </span>
        )}
        {running && (
          <span className='text-[11px] font-mono tabular-nums text-muted-foreground'>
            {runTime}s / ~{estSeconds}s
          </span>
        )}
      </div>
      {done && state.data ? (
        <div>
          <div className='flex items-end gap-2'>
            <span
              className='text-2xl font-black leading-none'
              style={{ color: GRADE_COLORS[state.data.grade] || '#64748b' }}
            >
              {state.data.overallScore}
            </span>
            <span
              className='text-xs font-bold px-1.5 py-0.5 rounded mb-0.5'
              style={{
                // color-mix, not a `+15` hex-alpha suffix: GRADE_COLORS
                // now returns theme-aware `var(--pm-grade-*)` strings.
                background: `color-mix(in srgb, ${GRADE_COLORS[state.data.grade]} 8%, transparent)`,
                color: GRADE_COLORS[state.data.grade],
              }}
            >
              {state.data.grade}
            </span>
          </div>
          {onRetry && (
            <button
              onClick={e => {
                e.stopPropagation()
                onRetry()
              }}
              className='flex items-center gap-1 text-[11px] mt-2 px-1.5 py-0.5 rounded-md border border-border transition-colors hover:brightness-125 text-muted-foreground'
            >
              <RefreshCw size={8} /> Re-verify
            </button>
          )}
        </div>
      ) : err ? (
        <div className='flex items-center justify-between'>
          <span className='text-xs text-cta-ink'>{state.error}</span>
          {onRetry && (
            <button
              onClick={e => {
                e.stopPropagation()
                onRetry()
              }}
              className='flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-border transition-colors hover:brightness-125 text-muted-foreground'
            >
              <RefreshCw size={9} /> Retry
            </button>
          )}
        </div>
      ) : running ? (
        <div>
          <div className='h-1.5 rounded-full overflow-hidden mt-1 bg-muted'>
            <div
              className='h-full rounded-full transition-all duration-500 ease-out'
              style={{
                background: color,
                width: `${Math.min(95, (runTime / estSeconds) * 100)}%`,
              }}
            />
          </div>
          <span className='block text-[11px] mt-1 text-muted-foreground tabular-nums'>
            ~{Math.max(0, estSeconds - runTime)}s remaining
          </span>
        </div>
      ) : state.status === 'queued' ? (
        <span className='text-[11px] text-muted-foreground'>
          Queued (~{estSeconds}s)
        </span>
      ) : state.status === 'skipped' ? (
        <span className='text-[11px] text-muted-foreground'>Skipped</span>
      ) : null}
    </div>
  )
}
